import axios from 'axios'
import { Message } from '@arco-design/web-vue'
import router from '@/router'

// 从环境变量获取 API 基础 URL，如果没有设置则使用默认值
let API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// 清理和规范化 baseURL，避免重复拼接
if (API_BASE_URL) {
  // 检测并修复重复域名的情况（如：https://domain1.com/domain2.com）
  const urlPattern = /(https?:\/\/[^\/]+)(?:\/[^\/]+\.(?:com|net|org|io|cn))+/g
  if (urlPattern.test(API_BASE_URL)) {
    // 提取第一个有效的域名
    const match = API_BASE_URL.match(/^(https?:\/\/[^\/]+)/)
    if (match) {
      API_BASE_URL = match[1]
    }
  }

  // 移除末尾的斜杠
  API_BASE_URL = API_BASE_URL.replace(/\/+$/, '')

  // 如果是完整 URL（包含协议）
  if (API_BASE_URL.startsWith('http://') || API_BASE_URL.startsWith('https://')) {
    // 检查是否已经包含 /api，如果没有则添加
    if (!API_BASE_URL.endsWith('/api')) {
      API_BASE_URL = API_BASE_URL + '/api'
    }
  } else {
    // 相对路径，确保以 / 开头
    if (!API_BASE_URL.startsWith('/')) {
      API_BASE_URL = '/' + API_BASE_URL
    }
    // 如果相对路径不包含 /api，添加它
    if (!API_BASE_URL.includes('/api')) {
      API_BASE_URL = API_BASE_URL === '/' ? '/api' : API_BASE_URL + '/api'
    }
  }
} else {
  API_BASE_URL = '/api'
}

// 调试日志（开发环境）
if (import.meta.env.DEV) {
  console.log('API Base URL:', API_BASE_URL)
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 10分钟超时（如获取STS等接口）
  headers: {
    'Content-Type': 'application/json'
  }
})

const isSilentErrorRequest = (config: any) => {
  if (config?.silentError === true) return true
  const headers = config?.headers
  const value = headers?.get?.('X-Silent-Error') ?? headers?.['X-Silent-Error']
  return value === 'true'
}

const silentErrorConfig = { silentError: true } as any

// Request interceptor - add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => {
    // blob 响应（如文件导出）直接返回
    if (response.config.responseType === 'blob') {
      return response
    }

    const data = response.data

    // 检查响应格式：code = 200 或 code = 0 表示成功（兼容字符串 "200"）
    if (data.code == 200 || data.code == 0) {
      // code = 200 或 0 表示成功，不显示任何消息，直接返回数据
      if (data.data !== undefined) {
        // 如果 data.data 是数组，直接返回数组
        if (Array.isArray(data.data)) {
          return data.data
        }
        // 如果 data.data 是对象（如包含 list、total 等的分页响应），直接返回对象
        // 同时保留顶层的 summary 字段（如果存在且 data.data 中没有）
        const result = { ...data.data }
        // 如果顶层有 summary 且 data.data 中没有，则添加
        if (data.summary !== undefined && result.summary === undefined) {
          result.summary = data.summary
        }
        return result
      } else {
        return data
      }
    } else {
      // code !== 200 且 code !== 0 表示失败，显示错误信息
      const errorMessage = data.message || '请求失败'
      const suppressErrorMessage = Boolean(
        (response.config as any)?.suppressErrorMessage
      )

      // 特殊处理 401，即使 HTTP 状态码是 200
      if (data.code === 401) {
        Message.error('登录已过期，请重新登录')
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('menus')
        localStorage.removeItem('permissions')
        localStorage.removeItem('tablePreferences')
        router.push('/login')
        return Promise.reject(new Error('Unauthorized'))
      }

      if (!isSilentErrorRequest(response.config)) {
        Message.error(errorMessage)
      }
      return Promise.reject(new Error(errorMessage))
    }
  },
  (error) => {
    // 主动取消的请求不弹错误提示
    if (
      axios.isCancel(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError'
    ) {
      return Promise.reject(error)
    }

    const silent = isSilentErrorRequest(error.config)

    if (error.response) {
      const status = error.response.status
      const data = error.response.data

      switch (status) {
        case 401:
          Message.error(data.message || '登录已过期，请重新登录')
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          localStorage.removeItem('menus')
          localStorage.removeItem('permissions')
          localStorage.removeItem('tablePreferences')
          router.push('/login')
          break
        case 403:
          if (!silent) Message.error(data.message || '没有权限访问')
          break
        case 404:
          if (!silent) Message.error(data.message || '资源不存在')
          break
        case 422:
          if (!silent) Message.error(data.message || '请求参数错误')
          break
        default:
          if (!silent) Message.error(data.message || '请求失败')
      }
    } else if (error.request) {
      if (!silent) Message.error('网络连接失败，请检查后端服务是否启动')
    } else {
      if (!silent) Message.error(error.message || '请求配置错误')
    }
    return Promise.reject(error)
  }
)

export default api


// ============ Auth APIs ============
export const authApi = {
  login(username: string, password: string) {
    return api.post('/auth/login', { username, password })
  },

  logout() {
    return api.post('/auth/logout')
  },

  getUserInfo() {
    return api.get('/auth/getUserInfo')
  },

  getPermCode() {
    return api.get('/auth/getPermCode')
  },

  getMenus() {
    return api.get('/menus/user')
  },

  changePassword(data: {
    old_password: string
    new_password: string
    confirm_password?: string
  }) {
    // 兼容线上已放行的 change-password；同时带上 confirm_password
    return api.post('/auth/change-password', {
      old_password: data.old_password,
      new_password: data.new_password,
      confirm_password: data.confirm_password ?? data.new_password,
    })
  }
}


// ============ User APIs ============
export const userApi = {
  getList(params: {
    page?: number
    page_size?: number
    role?: number | number[] // 角色ID或角色ID数组
    status?: string
    keyword?: string
  }) {
    return api.get('/users', { params })
  },

  create(data: {
    username: string
    password: string
    name: string
    phone?: string
    email?: string
    role?: number | number[] // 角色ID或角色ID数组
    status?: string
  }) {
    return api.post('/users', data)
  },

  update(id: number, data: {
    name?: string
    phone?: string
    email?: string
    role?: number | number[] // 角色ID或角色ID数组
    status?: string
  }) {
    return api.put(`/users/${id}`, data)
  },

  getById(id: number) {
    return api.get(`/users/${id}`)
  },

  delete(id: number) {
    return api.delete(`/users/${id}`)
  },

  /**
   * 获取设计师列表
   * GET /api/users/designers
   */
  getDesigners(params?: {
    keyword?: string
  }) {
    return api.get('/users/designers', { params })
  },

  /**
   * 重置用户密码
   * POST /api/users/:id/reset-password
   */
  resetPassword(id: number) {
    return api.post(`/users/${id}/reset-password`)
  },

  syncFeishu() {
    return api.post('/users/sync-feishu')
  }
}


/** 广告账户列表接口：与 list 相同筛选条件下的全量汇总（非仅当前页） */
export interface AccountListSummary {
  /** 符合条件的账户总数 */
  total_count: number
  balance: number
  daily_spend: number
  lifetime_spend: number
  month_spend?: number
}

export interface AccountListResponse {
  list: any[]
  total: number
  page?: number
  page_size?: number
  summary?: AccountListSummary
}

// ============ Account APIs ============
export const accountApi = {
  getList(params: {
    page?: number
    page_size?: number
    platform?: string
    status?: string
    /** 名称等模糊/精准搜索 */
    keyword?: string
    /** 媒体账户 ID，多选时为逗号分隔（与推广页其它列表一致） */
    account_id?: string
    optimizer_id?: number | string
    /** 时区，多选逗号分隔 */
    timezone?: string
    start_date?: string
    end_date?: string
    /** 是否开启数据拉取：1 开启，0 关闭 */
    is_data_synced?: number
  }) {
    return api.get<AccountListResponse>('/accounts', { params })
  },

  getTimezoneOptions(platform?: string) {
    return api.get<{ list: string[] }>('/accounts/timezone-options', {
      params: platform ? { platform } : {},
    })
  },

  exportList(params: {
    platform?: string
    status?: string
    keyword?: string
    account_id?: string
    optimizer_id?: number | string
    timezone?: string
    start_date?: string
    end_date?: string
  }) {
    return api.get('/accounts/export', { params, responseType: 'blob' as const })
  },

  getById(id: number) {
    return api.get(`/accounts/${id}`)
  },

  sync(params: { platform?: string; account_id?: number }) {
    return api.post('/accounts/sync', params)
  },

  // 获取优化师列表（用于下拉选择）
  getOptimizers() {
    return api.get<Array<{ id: number; name: string; phone?: string; email?: string }>>('/organization/users')
  },

  // 分配优化师
  assignOptimizers(accountId: number, optimizerIds: number[]) {
    return api.put(`/accounts/${accountId}/optimizers`, {
      optimizer_ids: optimizerIds
    })
  },

  // 批量分配优化师
  batchAssignOptimizers(accountIds: number[], optimizerIds: number[]) {
    return api.post('/accounts/batch-assign-optimizers', {
      account_ids: accountIds,
      optimizer_ids: optimizerIds
    })
  },

  toggleDataSync(id: number, isDataSynced: number) {
    return api.put(`/accounts/${id}/data-sync`, { is_data_synced: isDataSynced })
  }
}


// ============ Campaign APIs ============
export const campaignApi = {
  getList(params: {
    page?: number
    pageSize?: number
    page_size?: number
    /** 平台广告账户 ID，多选时逗号拼接 */
    account_id?: string | number
    platform?: string
    status?: string
    objective?: string
    keyword?: string
    start_date?: string
    end_date?: string
    sort_field?: string
    sort_order?: 'asc' | 'desc'
  }) {
    // 统一参数名：优先使用 pageSize，兼容 page_size
    const requestParams: any = { ...params }
    if (requestParams.pageSize !== undefined) {
      requestParams.pageSize = requestParams.pageSize
    } else if (requestParams.page_size !== undefined) {
      requestParams.pageSize = requestParams.page_size
    }
    delete requestParams.page_size

    return api.get('/campaigns', { params: requestParams })
  },

  getById(id: number) {
    return api.get(`/campaigns/${id}`)
  },

  getTaskParams(id: string, options?: { silent?: boolean }) {
    return api.get(
      `/campaigns/${id}/task-params`,
      options?.silent
        ? ({ suppressErrorMessage: true } as any)
        : undefined,
    )
  },

  updateStatus(id: number, status: string) {
    return api.put(`/campaigns/${id}/status`, { status })
  },

  batchUpdateStatus(ids: (string | number)[], status: string, platform: string = 'facebook') {
    return api.post('/campaigns/batch-update-status', { ids, status, platform })
  },

  scheduleBatchEnable(
    ids: (string | number)[],
    executeAt: string,
    platform: string = 'facebook',
  ) {
    return api.post('/scheduled-enables', {
      level: 'campaign',
      ids,
      execute_at: executeAt,
      platform,
    })
  },

  batchUpdateBudget(ids: (string | number)[], budgetType: 'daily' | 'lifetime', budgetAmount: number, platform: string = 'facebook') {
    return api.post('/campaigns/batch-update-budget', { ids, budget_type: budgetType, budget_amount: budgetAmount, platform })
  },

  update(id: number, data: any) {
    return api.put(`/campaigns/${id}`, data)
  },

  delete(id: number) {
    return api.delete(`/campaigns/${id}`)
  },

  copy(id: number) {
    return api.get(`/campaigns/${id}/copy`)
  },

  /** 复制广告系列（异步任务，任务类型「复制广告系列」） */
  createCopyTask(id: string, params: {
    count: number
    platform?: string
    tracking_specs?: string[]
  }) {
    return api.post(`/campaigns/${encodeURIComponent(id)}/copy-task`, {
      count: params.count,
      platform: params.platform ?? 'facebook',
      ...(params.tracking_specs?.length
        ? { tracking_specs: params.tracking_specs }
        : {}),
    })
  },

  create(data: any) {
    return api.post('/campaigns', data)
  },

  /** 手动添加广告组：沿用系列配置，换受众包 + 素材 */
  addAdset(id: string, data: {
    creative_ids: (string | number)[]
    audience_package_id?: number | null
  }) {
    return api.post(`/campaigns/${encodeURIComponent(id)}/add-adset`, data)
  },
}


// ============ AdSet APIs ============
export const adsetApi = {
  getList(params: {
    page?: number
    pageSize?: number
    page_size?: number
    account_id?: string | number
    platform?: string
    campaign_id?: string | number
    adset_id?: string | number
    status?: string
    promote_type?: number
    os_type?: number
    drama_id?: number
    start_date?: string
    end_date?: string
    keyword?: string
    sort_field?: string
    sort_order?: 'asc' | 'desc'
  }) {
    // 统一参数名：优先使用 pageSize，兼容 page_size
    const requestParams: any = { ...params }
    if (requestParams.pageSize !== undefined) {
      requestParams.pageSize = requestParams.pageSize
    } else if (requestParams.page_size !== undefined) {
      requestParams.pageSize = requestParams.page_size
    }
    delete requestParams.page_size

    return api.get('/adsets', { params: requestParams })
  },

  copy(id: string, data: {
    account_ids: string[]
    /** 多主页按顺序填满；与 page_id 二选一或同时传（page_id 可为第一项） */
    page_ids?: string[]
    page_id?: string
    /** 开启后由后端扫描创建人有权限且 creatable_ads_count > 0 的主页 */
    auto_assign_pages?: boolean
    count: number
    adset_enabled?: boolean
    start_time?: string
    end_time?: string
    override_budget?: number
    budget_type?: 'daily' | 'lifetime'
    /** 定时创建：开启后到点再执行复制入队 */
    scheduled_create_enabled?: boolean
    scheduled_create_at?: string
  }) {
    return api.post(`/adsets/${id}/copy`, data)
  },

  batchUpdateStatus(ids: (string | number)[], status: string, platform: string = 'facebook') {
    return api.post('/adsets/batch-update-status', { ids, status, platform })
  },

  scheduleBatchEnable(
    ids: (string | number)[],
    executeAt: string,
    platform: string = 'facebook',
  ) {
    return api.post('/scheduled-enables', {
      level: 'adset',
      ids,
      execute_at: executeAt,
      platform,
    })
  },

  batchUpdateBudget(ids: (string | number)[], budgetType: 'daily' | 'lifetime', budgetAmount: number, platform: string = 'facebook') {
    return api.post('/adsets/batch-update-budget', { ids, budget_type: budgetType, budget_amount: budgetAmount, platform })
  },

  /** 手动添加广告（系统创建广告组） */
  addAd(id: string, data: { creative_ids: (string | number)[] }) {
    return api.post(`/adsets/${encodeURIComponent(id)}/add-ad`, data)
  },
}


// ============ Ad APIs ============
export const adApi = {
  getList(params: {
    page?: number
    pageSize?: number
    page_size?: number
    account_id?: string | number
    platform?: string
    campaign_id?: string | number
    adset_id?: string | number
    ad_id?: string | number
    status?: string
    sort_field?: string
    sort_order?: 'asc' | 'desc'
    promote_type?: number
    os_type?: number
    drama_id?: number
    start_date?: string
    end_date?: string
    keyword?: string
  }) {
    // 统一参数名：优先使用 pageSize，兼容 page_size
    const requestParams: any = { ...params }
    if (requestParams.pageSize !== undefined) {
      requestParams.pageSize = requestParams.pageSize
    } else if (requestParams.page_size !== undefined) {
      requestParams.pageSize = requestParams.page_size
    }
    delete requestParams.page_size

    return api.get('/ads', { params: requestParams })
  },

  copy(id: string, data: {
    account_ids: string[]
    page_id: string
    count: number
  }) {
    return api.post(`/ads/${id}/copy`, data)
  },

  batchUpdateStatus(ids: (string | number)[], status: string, platform: string = 'facebook') {
    return api.post('/ads/batch-update-status', { ids, status, platform })
  }
}

/** 批量同步到9楼（营销 tvadcenter / toonory）请求体单项 */
export interface NinthFloorSyncItem {
  type: string
  designer_name: string
  name: string
  file_url: string
  thumbnail_url: string
  file_size: string
  duration: string
  height: string
  width: string
  tags: { name: string; level: number }[]
  /** 剧名称（来自 toon_content.ts_dramas.dramas_name_cn） */
  drama_name: string
  /** 剧语言，如 en（来自 ts_dramas.lang） */
  drama_lang: string
}

const ninthFloorHttp = axios.create({
  timeout: 300000,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * 9楼同步接口完整 URL。
 * 优先 `VITE_NINTH_FLOOR_SYNC_URL`；否则正式 https://marketing.tvadcenter.com 、测试 http://marketing-qa.toonory.com + `VITE_NINTH_FLOOR_SYNC_PATH`。
 */
export function getNinthFloorSyncEndpoint(): string {
  const full = import.meta.env.VITE_NINTH_FLOOR_SYNC_URL?.trim()
  if (full) {
    return full.replace(/\/$/, '')
  }
  const base =
    import.meta.env.VITE_APP_ENV === 'production'
      ? 'https://marketing.tvadcenter.com'
      : 'http://marketing-qa.toonory.com'
  const path = (import.meta.env.VITE_NINTH_FLOOR_SYNC_PATH || '/api/creatives-batch-report').trim()
  const p = path.startsWith('/') ? path : `/${path}`
  return `${base.replace(/\/$/, '')}${p}`
}

export async function postNinthFloorBatchSync(payload: NinthFloorSyncItem[]): Promise<{
  code: number
  message: string
  data?: { success_num?: number; error_num?: number }
  timestamp?: number
}> {
  const url = getNinthFloorSyncEndpoint()
  const { data } = await ninthFloorHttp.post(url, payload)
  if (data.code !== 200 && data.code !== 0) {
    throw new Error(data.message || '同步失败')
  }
  return data
}

// ============ Creative APIs ============
// ============ Creative APIs ============
export const creativeApi = {
  getList(params: {
    page?: number
    pageSize?: number
    page_size?: number
    type?: string
    keyword?: string
    /** 多素材名精确匹配（数组；请求时会转为 names_text） */
    names?: string[]
    /** 多素材名原文，换行/逗号分隔 */
    names_text?: string
    designer_id?: string | number
    tags?: number[]
    start_date?: string
    end_date?: string
    approval_start_date?: string
    approval_end_date?: string
    work_id?: string | number
    size?: string
    rejected?: string
    sort_field?: string
    sort_order?: 'asc' | 'desc'
    /** 为 true 时仅返回允许在创建广告中选择的素材 */
    for_ad_creation?: boolean | number | string
    /** 配合 for_ad_creation：facebook 过滤 is_enabled_for_ad，tiktok 过滤 is_enabled_for_tiktok_ad */
    platform?: 'facebook' | 'tiktok' | string
    /** 为 0/false 时不关联 DWS 汇总花费与 ROI（素材管理页减轻负载）；创建广告选素材传 1 */
    include_dws_metrics?: boolean | number | string
  }) {
    const requestParams: any = { ...params }
    if (requestParams.pageSize !== undefined) {
      requestParams.pageSize = requestParams.pageSize
    } else if (requestParams.page_size !== undefined) {
      requestParams.pageSize = requestParams.page_size
    }
    delete requestParams.page_size

    // 处理 tags 数组
    if (requestParams.tags && Array.isArray(requestParams.tags)) {
      requestParams.tags = requestParams.tags.join(',')
    }

    // 多素材名：统一走 names_text，避免 GET 数组序列化差异
    if (Array.isArray(requestParams.names) && requestParams.names.length > 0) {
      requestParams.names_text = requestParams.names
        .map((n: unknown) => String(n ?? '').trim())
        .filter((n: string) => n !== '')
        .join('\n')
      delete requestParams.names
    }

    return api.get('/creatives', { params: requestParams })
  },

  getById(id: number) {
    return api.get(`/creatives/${id}`)
  },

  upload(data: FormData | {
    file_url: string
    thumbnail_url?: string
    name: string
    file_size: number
    width?: number
    height?: number
    duration?: number
    type: 'image' | 'video'
    designer_id?: number
    tags?: number[]
  }) {
    if (data instanceof FormData) {
      return api.post('/creatives/upload', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    } else {
      return api.post('/creatives/upload', data)
    }
  },

  update(id: number, data: {
    name?: string
    designer_id?: number
    tags?: number[]
    /** Facebook 创建广告是否可选：1/0 或布尔（仅超管/管理员/部门经理可改） */
    is_enabled_for_ad?: number | boolean
    /** TikTok 创建广告是否可选：1/0 或布尔（仅超管/管理员/部门经理可改） */
    is_enabled_for_tiktok_ad?: number | boolean
  }) {
    return api.put(`/creatives/${id}`, data)
  },

  delete(id: number) {
    return api.delete(`/creatives/${id}`)
  },

  batchDelete(ids: number[]) {
    return api.post('/creatives/batch-delete', { ids })
  },

  batchPushVideoUpload(
    ids: number[],
    tiktokAdvertiserIds?: string[],
    facebookAccountIds?: string[],
  ) {
    return api.post('/creatives/batch-push-video-upload', {
      ids,
      ...(tiktokAdvertiserIds !== undefined
        ? { tiktok_advertiser_ids: tiktokAdvertiserIds }
        : {}),
      ...(facebookAccountIds !== undefined
        ? { facebook_account_ids: facebookAccountIds }
        : {}),
    })
  },

  /** 手动批量推送可选：TikTok 广告主（数据权限内、当前用户为优化师） */
  listTiktokManualUploadAdvertisers(params?: { keyword?: string }) {
    return api.get('/creatives/tiktok-manual-upload-advertisers', { params })
  },

  /** 手动批量推送可选：当前用户可见且已绑定优化师、具备 Facebook OAuth 的账户 */
  listFacebookManualUploadAccounts() {
    return api.get('/creatives/facebook-manual-upload-accounts')
  },

  batchTriggerAutomation(ids: number[], planId: number) {
    return api.post('/creatives/batch-trigger-automation', { ids, plan_id: planId })
  },

  batchTag(ids: number[], tags: number[], action: 'attach' | 'detach' | 'sync' = 'attach') {
    return api.post('/creatives/batch-tag', { ids, tags, action })
  },

  incrementUsage(id: number) {
    return api.post(`/creatives/${id}/increment-usage`)
  },

  getDownloadUrls(ids: number[]) {
    return api.post('/creatives/download-urls', { ids })
  },

  /** 批量同步到9楼（直连 tvadcenter / toonory，见 {@link postNinthFloorBatchSync}） */
  batchSyncToNinthFloor(payload: NinthFloorSyncItem[]) {
    return postNinthFloorBatchSync(payload)
  },
}

// ============ Creative Compliance Review APIs ============
export const creativeComplianceApi = {
  getList(params: {
    page?: number
    pageSize?: number
    page_size?: number
    keyword?: string
    start_date?: string
    end_date?: string
    audit_status?: string
    review_status?: string
    blocked_only?: boolean | number | string
    allowed_only?: boolean | number | string
    designer_id?: string | number
  }) {
    const requestParams: any = { ...params }
    if (requestParams.pageSize === undefined && requestParams.page_size !== undefined) {
      requestParams.pageSize = requestParams.page_size
    }
    delete requestParams.page_size
    return api.get('/creatives/compliance-reviews', { params: requestParams })
  },

  getPendingCount() {
    return api.get('/creatives/compliance-reviews/pending-count', silentErrorConfig)
  },

  getDesigners() {
    return api.get('/creatives/compliance-reviews/designers', silentErrorConfig)
  },

  getDetail(id: number) {
    return api.get(`/creatives/${id}/compliance-review`)
  },

  setDecision(id: number, data: { decision: 'allowed' | 'blocked'; remark?: string }) {
    return api.post(`/creatives/${id}/compliance-review/decision`, data)
  },

  retry(id: number) {
    return api.post(`/creatives/${id}/compliance-review/retry`)
  },
}

// ============ Tag APIs ============
export const tagApi = {
  getList(params: {
    page?: number
    page_size?: number
    level?: number
    parent_id?: number | null
  }) {
    return api.get('/tags', { params })
  },

  getTree(params?: {
    level?: number
    max_level?: number
  }) {
    return api.get('/tags/tree', { params })
  },

  getParentOptions(level: number) {
    return api.get('/tags/parent-options', { params: { level } })
  },

  getById(id: number) {
    return api.get(`/tags/${id}`)
  },

  create(data: {
    level: number
    parent_id?: number | null
    name: string
    is_required?: boolean
    sort_order?: number
  }) {
    return api.post('/tags', data)
  },

  update(id: number, data: {
    name?: string
    parent_id?: number | null
    is_required?: boolean
    sort_order?: number
  }) {
    return api.put(`/tags/${id}`, data)
  },

  delete(id: number) {
    return api.delete(`/tags/${id}`)
  }
}


// ============ Task APIs ============
export const taskApi = {
  getList(params: {
    page?: number
    pageSize?: number
    page_size?: number
    creator_id?: string
    platform?: string
    status?: string
    start_date?: string
    end_date?: string
    /** 广告系列：纯数字=平台系列 ID；否则按系列名称模糊匹配（旧版单行输入） */
    campaign?: string
    /** 平台系列 ID，多个逗号分隔（与推广 CascadeFilterPanel 一致） */
    campaign_id?: string
    name_exact?: string
    include?: string
    exclude?: string
    /** 投放作品：与 form_data 内 workId / drama_id / dramaId 一致 */
    drama_id?: string
    /** 命名语言段（如 EN、JA），与 REPORT_LANGUAGE_FILTER_OPTIONS.value 一致 */
    lang?: string
    /** 任务 ID（支持片段模糊匹配） */
    task_id?: string
    /** 项目类型：1=APP 2=H5 */
    project_type?: string
    /** H5 站点 ID */
    site_id?: string
  }) {
    return api.get('/tasks', { params })
  },

  getDetail(id: string) {
    return api.get(`/tasks/${id}`)
  },

  create(data: any) {
    return api.post('/tasks', data)
  },

  retry(id: string, taskItemId?: number) {
    return api.post(`/tasks/${id}/retry`, { task_item_id: taskItemId })
  },

  retryItem(id: string, itemId: number) {
    return api.post(`/tasks/${id}/items/${itemId}/retry`)
  },

  cancel(id: string) {
    return api.post(`/tasks/${id}/cancel`)
  },

  getItems(id: string, params?: {
    page?: number
    pageSize?: number
    page_size?: number
    level?: string
    status?: string
  }) {
    return api.get(`/tasks/${id}/items`, { params })
  },

  copy(id: string, data: {
    account_ids: string[]
    page_id: string
    count: number
  }) {
    return api.post(`/tasks/${id}/copy`, data)
  }
}


// ============ Analytics APIs ============
export const analyticsApi = {
  getOverview(params?: {
    start_date?: string
    end_date?: string
  }) {
    return api.get('/analytics/overview', { params })
  },

  getReport(
    params: {
      page?: number
      page_size?: number
      dimensions?: string
      metrics?: string
      platform?: string
      start_date?: string
      end_date?: string
      account_id?: string | number
      campaign_id?: string | number
      adset_id?: string | number
      ad_id?: string | number
      promote_type?: number
      os_type?: number
      drama_id?: string | number
      /** 制作团队 ID，多个逗号分隔；与 drama_id 同时传时为交集 */
      production_team?: string | number
      admin_id?: string | number
      designer_id?: string | number
      lang?: string
      sort_field?: string
      sort_order?: 'asc' | 'desc'
      export?: boolean
    },
    config?: { signal?: AbortSignal },
  ) {
    return api.get('/analytics/report', { params, ...config })
  },

  getUtc8Report(
    params: {
      page?: number
      page_size?: number
      dimensions?: string
      metrics?: string
      platform?: string
      start_date?: string
      end_date?: string
      account_id?: string | number
      campaign_id?: string | number
      adset_id?: string | number
      ad_id?: string | number
      promote_type?: number
      os_type?: number
      drama_id?: string | number
      /** 制作团队 ID，多个逗号分隔；与 drama_id 同时传时为交集 */
      production_team?: string | number
      admin_id?: string | number
      designer_id?: string | number
      lang?: string
      sort_field?: string
      sort_order?: 'asc' | 'desc'
      /** true=导出文件流；false/不传=分页 JSON 查询 */
      export?: boolean
    },
    config?: { signal?: AbortSignal },
  ) {
    const isExport = params.export === true
    return api.get('/analyticsutc8/report', {
      params,
      ...(isExport ? { responseType: 'blob' as const } : {}),
      ...config,
    })
  },

  getUtc7Report(
    params: {
      page?: number
      page_size?: number
      dimensions?: string
      metrics?: string
      platform?: string
      start_date?: string
      end_date?: string
      account_id?: string | number
      campaign_id?: string | number
      adset_id?: string | number
      ad_id?: string | number
      promote_type?: number
      os_type?: number
      drama_id?: string | number
      /** 制作团队 ID，多个逗号分隔；与 drama_id 同时传时为交集 */
      production_team?: string | number
      admin_id?: string | number
      designer_id?: string | number
      lang?: string
      sort_field?: string
      sort_order?: 'asc' | 'desc'
      /** true=导出文件流；false/不传=分页 JSON 查询 */
      export?: boolean
    },
    config?: { signal?: AbortSignal },
  ) {
    const isExport = params.export === true
    return api.get('/analyticsutc7/report', {
      params,
      ...(isExport ? { responseType: 'blob' as const } : {}),
      ...config,
    })
  },

  getHourReport(
    params: {
      page?: number
      page_size?: number
      dimensions?: string
      metrics?: string
      platform?: string
      start_date?: string
      end_date?: string
      account_id?: string | number
      campaign_id?: string | number
      adset_id?: string | number
      ad_id?: string | number
      promote_type?: number
      os_type?: number
      drama_id?: string | number
      production_team?: string | number
      admin_id?: string | number
      designer_id?: string | number
      lang?: string
      sort_field?: string
      sort_order?: 'asc' | 'desc'
      export?: boolean
    },
    config?: { signal?: AbortSignal },
  ) {
    return api.get('/analyticshour/report', { params, ...config })
  },

  /**
   * 获取作品细分数据（按性别/年龄/国家/OS 拆分）
   * GET /api/analytics/breakdown
   */
  getBreakdown(params: {
    drama_id: string | number
    breakdown?: 'gender' | 'age' | 'country' | 'os'
    metrics?: string
    platform?: string
    start_date?: string
    end_date?: string
    account_id?: string
    campaign_id?: string
    adset_id?: string
    ad_id?: string
    admin_id?: string | number
    designer_id?: string | number
    promote_type?: number
    project_type?: string
    site_id?: string
  }) {
    return api.get('/analytics/breakdown', { params })
  },

  /**
   * 触发细分数据同步
   * POST /api/analytics/breakdown/sync
   */
  syncBreakdown(params: {
    date?: string
    breakdowns?: string[]
    platform?: string
    account_id?: string
  }) {
    return api.post('/analytics/breakdown/sync', params)
  },

  /**
   * 导出报表（后端导出，返回 CSV 文件）
   * 使用与 getReport 相同的筛选参数，加上 export=1
   */
  exportReport(params: {
    dimensions?: string
    metrics?: string
    platform?: string
    start_date?: string
    end_date?: string
    account_id?: string | number
    campaign_id?: string | number
    adset_id?: string | number
    ad_id?: string | number
    promote_type?: number
    os_type?: number
    drama_id?: string | number
    production_team?: string | number
    admin_id?: string | number
    designer_id?: string | number
    lang?: string
    sort_field?: string
    sort_order?: 'asc' | 'desc'
  }) {
    return api.get('/analytics/report', {
      params: { ...params, export: 1 },
      responseType: 'blob'
    })
  },

  exportHourReport(params: {
    dimensions?: string
    metrics?: string
    platform?: string
    start_date?: string
    end_date?: string
    account_id?: string | number
    campaign_id?: string | number
    adset_id?: string | number
    ad_id?: string | number
    promote_type?: number
    os_type?: number
    drama_id?: string | number
    production_team?: string | number
    admin_id?: string | number
    designer_id?: string | number
    lang?: string
    sort_field?: string
    sort_order?: 'asc' | 'desc'
  }) {
    return api.get('/analyticshour/report', {
      params: { ...params, export: 1 },
      responseType: 'blob'
    })
  }
}


// ============ Dashboard APIs ============
export const dashboardApi = {
  getStats() {
    return api.get('/dashboard/stats')
  },

  getSpendTrend(days: number = 7) {
    return api.get('/dashboard/spend-trend', { params: { days } })
  },

  getBalanceAlerts() {
    return api.get('/dashboard/balance-alerts')
  },

  getTodos() {
    return api.get('/dashboard/todos')
  }
}

type HomeProjectType = 'h5' | 'app'

type HomeScopeParams = {
  project_type?: HomeProjectType
  site?: string
  site_id?: string
  date_type?: 'today' | 'yesterday' | 'before_yesterday'
  date?: string
}

// ============ Home APIs ============
export const homeApi = {
  /**
   * 获取首页统计数据
   */
  getStats(params?: HomeScopeParams) {
    return api.get('/home/stats', { params })
  },

  /**
   * 获取数据趋势图数据
   * @param dimension 维度：total, platform, optimizer, designer, drama, os, language
   * @param metric 指标：spend, attributedUsers, recharge, revenue, roi, d0roi
   * @param startDate 开始日期：YYYY-MM-DD
   * @param endDate 结束日期：YYYY-MM-DD
   */
  getTrend(params: {
    dimension?: string
    metric?: string
    start_date?: string
    end_date?: string
  }) {
    return api.get('/home/trend', { params })
  },

  /**
   * 获取热门漫剧排行
   * @param startDate 开始日期：YYYY-MM-DD
   * @param endDate 结束日期：YYYY-MM-DD
   * @param limit 返回数量，默认5
   */
  getTopDramas(params?: {
    start_date?: string
    end_date?: string
    limit?: number
  }) {
    return api.get('/home/top-dramas', { params })
  },

  /**
   * 获取火爆素材排行
   * @param startDate 开始日期：YYYY-MM-DD
   * @param endDate 结束日期：YYYY-MM-DD
   * @param limit 返回数量，默认5
   */
  getTopMaterials(params?: {
    start_date?: string
    end_date?: string
    limit?: number
  }) {
    return api.get('/home/top-materials', { params })
  },

  /**
   * 首页「作品」卡片（兼容）：推荐 + 待测一次返回。
   */
  getWorks(params?: HomeScopeParams & {
    testing_page?: number
    testing_page_size?: number
    /** 待测作品语言（ts_dramas.lang，与 DramaController 一致，如 EN；不传或空为全部） */
    testing_lang?: string
  }) {
    return api.get<{
      recommended: Array<{
        drama_id: number
        name: string
        lang?: string
        lang_label?: string
        d0_roi_activ: number
        running_campaign_count: number
      }>
      testing: {
        list: Array<{
          drama_id: number
          name: string
          lang?: string
          lang_label?: string
          running_campaign_count: number
          release_time: string
        }>
        total: number
        page: number
        page_size: number
      }
    }>('/home/works', { params })
  },

  /** 首页推荐作品：按时间范围聚合，按 D0 ROI（激活）降序前 10 */
  getRecommendedWorks(params?: HomeScopeParams & {
    /** today=今天（默认），last_3_days=近3天 */
    time_range?: "today" | "last_3_days"
  }) {
    return api.get<{
      recommended: Array<{
        drama_id: number
        name: string
        lang?: string
        lang_label?: string
        d0_roi_activ: number
        running_campaign_count: number
      }>
    }>('/home/works/recommended', { params })
  },

  /** 首页待测作品：历史总花费低于阈值且不在排除名单；分页 */
  getTestingWorks(params?: HomeScopeParams & {
    testing_page?: number
    testing_page_size?: number
    testing_lang?: string
  }) {
    return api.get<{
      list: Array<{
        drama_id: number
        name: string
        lang?: string
        lang_label?: string
        running_campaign_count: number
        release_time: string
      }>
      total: number
      page: number
      page_size: number
    }>('/home/works/testing', { params })
  }
}


// ============ OAuth APIs ============
export const oauthApi = {
  // 获取用户授权状态
  getStatus() {
    return api.get('/oauth/status')
  },

  // 发起Facebook授权
  authorizeFacebook(userId: number) {
    return api.get('/oauth/facebook/authorize', { params: { user_id: userId } })
  },

  // 取消Facebook授权
  cancelFacebook() {
    return api.delete('/oauth/facebook')
  },

  // 发起TikTok授权
  authorizeTiktok(userId: number) {
    return api.get('/oauth/tiktok/authorize', { params: { user_id: userId } })
  },

  // 取消TikTok授权
  cancelTiktok() {
    return api.delete('/oauth/tiktok')
  },

  // 发起Google授权
  authorizeGoogle(userId: number) {
    return api.get('/oauth/google/authorize', { params: { user_id: userId } })
  },

  // 取消Google授权
  cancelGoogle() {
    return api.delete('/oauth/google')
  }
}


// ============ Facebook Assets APIs ============
export const facebookApi = {
  // 主页
  getPages() {
    return api.get('/facebook/pages')
  },
  syncPages() {
    return api.post('/facebook/pages/sync')
  },

  // 像素
  getPixels() {
    return api.get('/facebook/pixels')
  },
  syncPixels() {
    return api.post('/facebook/pixels/sync')
  },

  // 应用
  getApps() {
    return api.get('/facebook/apps')
  },
  syncApps() {
    return api.post('/facebook/apps/sync')
  },

  // 广告账户
  getAdAccounts() {
    return api.get('/facebook/ad-accounts')
  },
  syncAdAccounts() {
    return api.post('/facebook/ad-accounts/sync')
  },

  // Custom Audiences
  getAudiences(accountId: string) {
    return api.get(`/facebook/ad-accounts/${accountId}/audiences`)
  },
  syncAudiences(accountId: string) {
    return api.post(`/facebook/ad-accounts/${accountId}/audiences/sync`)
  }
}

export const customAudienceApi = {
  getList(accountId: string) {
    return api.get('/custom-audiences', { params: { account_id: accountId } })
  },
  sync(accountId: string) {
    return api.post('/custom-audiences/sync', { account_id: accountId })
  }
}


// ============ Pixel APIs (统一像素管理) ============
// ============ App APIs ============
export const appApi = {
  getList(params: {
    page?: number
    page_size?: number
    platform?: string
    account_id?: number
    keyword?: string
  }) {
    return api.get('/apps', { params })
  },

  getById(id: number) {
    return api.get(`/apps/${id}`)
  },

  sync(params?: { platform?: string; account_id?: number }) {
    return api.post('/apps/sync', params || {})
  }
}

// ============ Page APIs ============
export const pageApi = {
  getList(params: {
    page?: number
    page_size?: number
    platform?: string
    account_id?: number
    /** 名称或 page_id 模糊搜索（服务端 applyPagesKeywordFilter） */
    keyword?: string
    user_id?: number | string
    include_disabled?: boolean
    /** 仅 Facebook：仅返回 is_enabled=true 的主页（创建广告页用） */
    enabled_only?: 0 | 1 | boolean
  }) {
    return api.get('/pages', { params })
  },

  getUserOptions(params?: { platform?: string }) {
    return api.get('/pages/user-options', { params })
  },

  getById(id: number) {
    return api.get(`/pages/${id}`)
  },

  /** Facebook：主页资料 + 已发布帖子（服务端自动使用主页 Token，不返回 Token）。refresh=1 强制打 Graph */
  getLiveDetail(id: number, params?: { refresh?: 0 | 1 | boolean }) {
    return api.get(`/pages/${id}/live-detail`, { params })
  },

  sync(params?: { platform?: string; account_id?: number; user_id?: number }) {
    return api.post('/pages/sync', params || {})
  },

  /** 管理员：按 ads_volume?page_id= 刷新主页广告占用（默认全库 Facebook 主页） */
  syncAdsVolume(params?: { page_ids?: number[] }) {
    return api.post('/pages/sync-ads-volume', params || {})
  },

  update(id: number, data: { is_enabled?: boolean; enabled?: boolean }) {
    return api.put(`/pages/${id}`, data)
  },

  /** 管理员 / 部门经理：手动调整 Facebook 主页可创建广告数 */
  adjustCreatableAdsCount(
    id: number,
    data: { creatable_ads_count: number; reason: string },
  ) {
    return api.put(`/pages/${id}/creatable-ads-count`, data)
  },

  /** 主页可创建广告数调整记录（所有登录用户可查看） */
  getCreatableAdjustLogs(params?: {
    page?: number
    page_size?: number
    page_id?: string
    page_row_id?: number
    user_id?: number | string
    keyword?: string
  }) {
    return api.get('/pages/creatable-adjust-logs', { params })
  },

  assignToUsers(id: number, userIds: number[]) {
    return api.post(`/pages/${id}/assign`, { user_ids: userIds })
  },

  /** 按 page_id 批量分配给单个用户（每行一个 page_id） */
  batchAssignToUser(data: {
    user_id: number
    page_ids?: string[]
    page_ids_text?: string
    platform?: string
  }) {
    return api.post('/pages/batch-assign', data)
  },

  /** IG 权限广告 - 拉取可用广告账户（普通用户为自己账户；管理员代操作时为所属用户账户） */
  getIgPermissionAdAccounts(pageIds: number[]) {
    return api.post('/pages/ig-permission-ad/accounts', { page_ids: pageIds })
  },

  /** IG 权限广告 - 按固定参数批量创建 FB 1-1-1 任务 */
  createIgPermissionAds(data: { page_ids: number[]; account_ids: string[] }) {
    return api.post('/pages/ig-permission-ad/create', data)
  },
}

// ============ Pixel APIs ============
export const pixelApi = {
  getList(params: {
    page?: number
    page_size?: number
    platform?: string
    keyword?: string
    name?: string
    pixel_id?: string
    remark?: string
    include_disabled?: boolean
  }) {
    return api.get('/pixels', { params })
  },

  getById(id: number) {
    return api.get(`/pixels/${id}`)
  },



  update(id: number, data: { pixel_token?: string; remark?: string; is_enabled?: boolean }) {
    return api.put(`/pixels/${id}`, data)
  },

  sync(params?: { platform?: string; account_id?: number }) {
    return api.post('/pixels/sync', params || {})
  }
}


// ============ Geo Location APIs ============
export interface GeoLocation {
  key: string
  name: string
  type: string
  country_code?: string
  country_name?: string
  region?: string
  region_id?: string
  primary_city?: string
  primary_city_id?: string
}

export interface LocaleItem {
  key: string
  name: string
}

export interface TargetingItem {
  id: string
  name: string
  type: string
  audience_size_lower_bound?: number
  audience_size_upper_bound?: number
  path?: string[]
}

export const geoApi = {
  // 搜索地区
  search(params: {
    q: string
    location_types?: string
    limit?: number
  }) {
    return api.get<{ items: GeoLocation[], total: number }>('/geo/search', { params })
  },

  // 获取地区类型标签
  getLocationTypes() {
    return api.get<Record<string, string>>('/geo/location-types')
  },

  // 搜索语言
  searchLocales(params: { q: string, limit?: number }) {
    return api.get<{ items: LocaleItem[], total: number }>('/geo/locales', { params })
  },

  // 按 key 批量解析地区名称（受众包回显）
  resolveLocations(keys: string[]) {
    return api.post<{ items: GeoLocation[], total: number }>('/geo/resolve-locations', { keys })
  },

  // 按 key 批量解析语言名称（受众包回显）
  resolveLocales(keys: string[]) {
    return api.post<{ items: LocaleItem[], total: number }>('/geo/resolve-locales', { keys })
  },

  // 搜索细分定位（兴趣/行为等）；默认 mixed 多路合并，与广告后台建议一致
  searchTargeting(params: { q: string, target_type?: string, limit?: number }) {
    return api.get<{ items: TargetingItem[], total: number }>('/geo/targeting', { params })
  }
}

/** TikTok Smart Plus API */
export interface TikTokSmartPlusTargetingTag {
  geo: Record<string, unknown> | null
  isp: {
    isp_id: string
    location_id: string
    region_code: string
  } | null
  name: string
  status_info: {
    reason: "OFFLINE" | "NOT_SUPPORTED" | null
    status: "ENABLED" | "DISABLED"
  }
  targeting_type: string
}

export interface TikTokSmartPlusTargetingData {
  parent_tags: unknown[]
  targeting_tag_list: TikTokSmartPlusTargetingTag[]
}

export interface TikTokSmartPlusTargetingSearchParams {
  advertiser_id?: string
  targeting_type: 'INTEREST_AND_BEHAVIOR'
  search_keywords?: string
}

export const tiktokSmartPlusApi = {

  createTask(params: any){
    return api.post('/tiktok-smart-plus/tasks', params)
  },

  /** Get locations available to the specified TikTok advertiser. */
  getRegions(params: {
    advertiser_id?: string
    objective_type?: string
    placements?: string // JSON-encoded placement array
    level_range?: "ALL" | "TO_COUNTRY" | "TO_PROVINCE" | "TO_CITY"
    language?: string
  }) {
    return api.get('/tiktok-smart-plus/regions', { params })
  },
  /** Get ISP targeting options for the selected advertiser and locations.
   * location_ids 只有country级别的才会返回ISP选项
  */
  getTargeting(params: { advertiser_id?: string, location_ids: string }) {
    return api.get<TikTokSmartPlusTargetingData>(
      '/tiktok-smart-plus/targeting',
      { params },
    )
  },
  /** Get CTA button recommendations for the specified advertiser. */
  getCtaRecommend(params: {
    advertiser_id: string
    objective_type?: string
    promotion_type?: string
    language?: string
  }) {
    return api.get('/tiktok-smart-plus/cta/recommend', { params })
  },
  /** Get TikTok account identities for the specified advertiser. */
  getIdentities(params: { advertiser_id: string }) {
    return api.get('/tiktok-smart-plus/identity', { params })
  },
  /** Get supported OS versions for the specified TikTok advertiser. */
  getOsVersions(params: {
    advertiser_id?: string
    os_type: 'ANDROID' | 'IOS'
  }) {
    return api.get('/tiktok-smart-plus/os-versions', { params })
  },
  /** Get supported device models for the specified TikTok advertiser. */
  getDeviceModels(params: { advertiser_id?: string } = {}) {
    return api.get('/tiktok-smart-plus/device-models', { params })
  },
  /** Search or list interest and behavior targeting options. */
  searchTargeting(params: TikTokSmartPlusTargetingSearchParams) {
    return api.get('/tiktok-smart-plus/targeting-search', { params })
  },
  /** 查询 TikTok 广告账户下可用的自定义受众列表 */
  getCustomAudiences(params: {
    advertiser_id: string
    page?: number
    page_size?: number
  }) {
    return api.get('/tiktok-smart-plus/custom-audiences', { params })
  },
}


// ============ Role APIs ============
export const roleApi = {
  getList(params: {
    page?: number
    page_size?: number
    keyword?: string
    status?: string
  }) {
    return api.get('/roles', { params })
  },

  getAll() {
    return api.get('/roles/all')
  },

  getById(id: number) {
    return api.get(`/roles/${id}`)
  },

  create(data: {
    name: string
    code: string
    description?: string
    data_scope?: string
    status?: string
  }) {
    return api.post('/roles', data)
  },

  update(id: number, data: {
    name?: string
    description?: string
    data_scope?: string
    status?: string
  }) {
    return api.put(`/roles/${id}`, data)
  },

  delete(id: number) {
    return api.delete(`/roles/${id}`)
  },

  getMenus(id: number) {
    return api.get(`/roles/${id}/menus`)
  },

  assignMenus(id: number, menuIds: number[]) {
    return api.put(`/roles/${id}/menus`, { menu_ids: menuIds })
  },

  getPermissions(id: number) {
    return api.get(`/roles/${id}/permissions`)
  },

  assignPermissions(id: number, permissionIds: number[]) {
    return api.put(`/roles/${id}/permissions`, { permission_ids: permissionIds })
  },

  getMetricPermissions(id: number) {
    return api.get(`/roles/${id}/metrics`, silentErrorConfig)
  },

  assignMetricPermissions(
    id: number,
    metrics: {
      attributes: string[]
      media: string[]
      internal: string[]
      ltv_first: string[]
      ltv_last: string[]
    }
  ) {
    // 后端按类别接收指标权限
    return api.post('/roles/metrics', { role_id: id, ...metrics }, silentErrorConfig)
  }
}


// ============ Menu APIs ============
export const menuApi = {
  getList(params?: {
    page?: number
    page_size?: number
    keyword?: string
    status?: string
  }) {
    return api.get('/menus/list', { params })
  },

  getTree() {
    return api.get('/menus/tree')
  },

  getOptions() {
    return api.get('/menus/options')
  },

  getById(id: number) {
    return api.get(`/menus/${id}`)
  },

  create(data: {
    parent_id?: number
    name: string
    title: string
    path?: string
    component?: string
    icon?: string
    sort?: number
    type: string
    status?: string
    hidden?: boolean
    keep_alive?: boolean
    affix?: boolean
  }) {
    return api.post('/menus', data)
  },

  update(id: number, data: {
    parent_id?: number
    name?: string
    title?: string
    path?: string
    component?: string
    icon?: string
    sort?: number
    type?: string
    status?: string
    hidden?: boolean
    keep_alive?: boolean
    affix?: boolean
  }) {
    return api.put(`/menus/${id}`, data)
  },

  delete(id: number) {
    return api.delete(`/menus/${id}`)
  },

  updateStatus(id: number, status: string) {
    return api.put(`/menus/${id}/status`, { status })
  },

  updateSort(menus: Array<{ id: number; sort: number }>) {
    return api.put('/menus/sort', { menus })
  }
}


// ============ Default Metric APIs ============
export const defaultMetricApi = {
  get(params?: { type?: string }) {
    return api.get('/default-metrics', { params })
  },

  update(data: {
    columns: string[]
  }) {
    return api.put('/default-metrics', data)
  }
}

// ============ Column Setting APIs ============
export const columnSettingApi = {
  get(pageKey: string, template?: string, templateId?: number) {
    return api.get(`/column-settings/${pageKey}`, {
      params: {
        ...(template ? { template } : {}),
        ...(templateId && templateId > 0 ? { template_id: templateId } : {}),
      },
    })
  },

  getTemplates(pageKey: string) {
    return api.get(`/column-settings/${pageKey}/templates`)
  },

  update(pageKey: string, data: {
    columns: (string | { key: string; width?: number })[]
    template_name?: string
    template_id?: number
    is_last_used?: boolean // 是否是最后使用的模板（点击"保存并使用"时设置为 true）
  }) {
    return api.put(`/column-settings/${pageKey}`, data)
  },

  deleteTemplate(pageKey: string, templateNameOrId: string | number) {
    // URL 编码模板名称，避免特殊字符和中文导致的问题；优先传数字 id
    const encoded = encodeURIComponent(String(templateNameOrId))
    return api.delete(`/column-settings/${pageKey}/templates/${encoded}`)
  }
}


// ============ Organization APIs ============
export interface Department {
  id: number
  name: string
  description?: string
  manager_id?: number
  manager?: {
    id: number
    name: string
    username: string
  }
  member_count?: number
  teams?: Team[]
}

export interface Team {
  id: number
  department_id: number
  name: string
  leader_id?: number
  leader_name?: string
  description?: string
  member_count?: number
}

export interface Member {
  id: number
  name: string
  phone?: string
  email?: string
  role?: string
  status?: string
  department_id?: number
  department_name?: string
  team_id?: number
  team_name?: string
}

export const organizationApi = {
  // 部门相关
  getDepartments() {
    return api.get<Department[]>('/organization/departments')
  },

  createDepartment(data: { name: string; manager_id?: number; description?: string }) {
    return api.post<Department>('/organization/departments', data)
  },

  updateDepartment(id: number, data: { name?: string; manager_id?: number; description?: string }) {
    return api.put<Department>(`/organization/departments/${id}`, data)
  },

  deleteDepartment(id: number) {
    return api.delete(`/organization/departments/${id}`)
  },

  // 小组相关
  createTeam(data: { department_id: number; name: string; leader_id?: number; description?: string }) {
    return api.post<Team>('/organization/teams', data)
  },

  updateTeam(id: number, data: { name?: string; leader_id?: number; description?: string }) {
    return api.put<Team>(`/organization/teams/${id}`, data)
  },

  deleteTeam(id: number) {
    return api.delete(`/organization/teams/${id}`)
  },

  // 成员相关
  getMembers(params: {
    page?: number
    page_size?: number
    department_id?: number
    team_id?: number
    role?: string
    status?: string
    keyword?: string
  }) {
    return api.get<{ list: Member[], total: number, page: number, page_size: number }>('/organization/members', { params })
  },

  adjustMemberOrg(id: number, data: { department_id?: number | null; team_id?: number | null }) {
    return api.put(`/organization/members/${id}/adjust-org`, data)
  },

  // 用户列表（用于下拉选择）
  getAllUsers() {
    return api.get<Array<{ id: number; name: string; phone?: string; email?: string }>>('/organization/users')
  }
}


// ============ Dramas APIs (投放作品) ============
export interface Drama {
  id: number
  series_id: number
  dramas_name_cn: string
  lang: string
  lang_label: string
  cover: string
  status: number
  serial_status: number
  number_of_episodes: number
  watch_num: number
  /** 原始上映时间（库内字段，可能为时间戳或日期串） */
  released_time?: number | string
  /** 与首页/飞书日报一致的上映日 Y-m-d */
  release_time?: string
  /** 男女频（ts_series.oriented）：0=通用 1=男频 2=女频 */
  oriented?: 0 | 1 | 2 | number
  /** 制作团队 ID，与 ts_dramas.production_team 一致 */
  production_team?: number
  production_team_label?: string
  /** 是否已标记为已测（不再出现在首页待测作品） */
  is_testing_marked?: boolean
}

export const dramasApi = {
  getList(params: {
    page?: number
    page_size?: number
    /** 不传时后端默认已上架+隐藏；可传单个或数组 */
    status?: number | number[]
    lang?: string
    /** @deprecated 请用 drama_id / user_title / intro */
    keyword?: string
    drama_id?: string | number
    /** 用户显示标题（仅搜内部标题 + 用户标题，不含简介） */
    user_title?: string
    /** 简介 */
    intro?: string
    /** 男女频（ts_series.oriented）：0=通用 1=男频 2=女频；不传为全部 */
    oriented?: 0 | 1 | 2 | "0" | "1" | "2"
    /** 制作团队 ID，多个逗号分隔 */
    production_team?: string
    /** 上映时间区间（含），与 ts_dramas.released_time 对应，Asia/Shanghai 自然日 */
    release_start_date?: string
    release_end_date?: string
    /** H5 站点 ID，多个逗号分隔 */
    site_id?: string
  }) {
    return api.get<{ list: Drama[], total: number, page: number, pageSize: number }>('/dramas', { params })
  },

  getById(id: number) {
    return api.get<Drama>(`/dramas/${id}`)
  },

  getEpisodes(id: number) {
    return api.get(`/dramas/${id}/episodes`)
  },

  downloadEpisode(episodeId: number, options?: { variant?: '1080' | '720' }) {
    return api.get(`/dramas/episodes/${episodeId}/download`, {
      params: options,
      responseType: 'blob',
    })
  },

  downloadEpisodesBatch(
    episodeIds: number[],
    dramaName: string,
    options?: { variant?: '1080' | '720' },
  ) {
    return api.post('/dramas/episodes/download-batch', {
      episode_ids: episodeIds,
      drama_name: dramaName,
      ...options,
    }, { responseType: 'blob' })
  },

  getDownloadUrls(episodeIds: number[], options?: { variant?: '1080' | '720' }) {
    return api.post('/dramas/episodes/download-urls', {
      episode_ids: episodeIds,
      ...options,
    })
  },

  /** AI 剪辑：后端转发内网 POST …/api/automation/jobs */
  createAutomationJob(body: {
    work_id: string
    plan_count?: number
    render_limit?: number
    publish_after_render?: boolean
    /** 素材类型：mixed/commentary_first/original_short/original_long */
    ad_type?: string
    /** 是否过滤违规素材 */
    filter_violation?: boolean
    /** 过滤等级（默认 L2） */
    violation_level?: 'L2' | string
    /** 水印与片尾策略：both/watermark_only/tail_only/none */
    watermark_tail_mode?: string
  }) {
    return api.post<unknown>('/dramas/automation/jobs', body)
  },

  markTesting(id: number) {
    return api.post<{ drama_id: number; is_testing_marked: boolean }>(`/dramas/${id}/testing-mark`)
  },

  unmarkTesting(id: number) {
    return api.delete<{ drama_id: number; is_testing_marked: boolean }>(`/dramas/${id}/testing-mark`)
  },
}

// ============ Filter Options APIs (筛选选项) ============
export interface FilterOption {
  id: string
  name: string
  [key: string]: any
}

// ============ Upload APIs ============
export const uploadApi = {
  getStsToken(params: {
    file_type?: string
    file_name?: string
    category_name?: string
    subcategory_name?: string
    /** 大文件/视频上传时传 true，可获取 12 小时有效期的 STS，避免上传途中过期 */
    long_upload?: boolean
  }) {
    return api.post('/upload/sts-token', params)
  },

  uploadCallback(params: {
    object_key: string
    file_size?: number
    file_type?: string
  }) {
    return api.post('/upload/callback', params)
  }
}

// ============ Filter Option APIs ============
export const filterOptionApi = {
  // 获取广告系列筛选选项（支持 campaign_id 搜索）
  getCampaigns(params: {
    account_id?: string
    platform?: string
    keyword?: string
    include?: string
    exclude?: string
    campaign_id?: string
    name_exact?: string
    promote_type?: number
    page?: number
    page_size?: number
  }) {
    return api.get<{ list: FilterOption[], total: number }>('/filter-options/campaigns', { params })
  },

  // 获取广告组筛选选项（支持 adset_id 搜索）
  getAdsets(params: {
    account_id?: string
    campaign_id?: string
    platform?: string
    keyword?: string
    name_exact?: string
    include?: string
    exclude?: string
    adset_id?: string
    promote_type?: number
    page?: number
    page_size?: number
  }) {
    return api.get<{ list: FilterOption[], total: number }>('/filter-options/adsets', { params })
  },

  // 获取广告筛选选项（支持 ad_id 搜索）
  getAds(params: {
    account_id?: string
    campaign_id?: string
    adset_id?: string
    platform?: string
    keyword?: string
    name_exact?: string
    include?: string
    exclude?: string
    ad_id?: string
    promote_type?: number
    page?: number
    page_size?: number
  }) {
    return api.get<{ list: FilterOption[], total: number }>('/filter-options/ads', { params })
  },

  // 获取投放作品筛选选项
  getDramas(params: {
    keyword?: string
    lang?: string
    status?: number
    /** H5 站点 ID */
    site_id?: string
    /** 男女频（ts_series.oriented）：0=通用 1=男频 2=女频；不传为全部 */
    oriented?: 0 | 1 | 2 | "0" | "1" | "2"
    /** 制作团队 ID，多个逗号分隔 */
    production_team?: string
    page?: number
    page_size?: number
  }) {
    return api.get<{ list: FilterOption[], total: number }>('/filter-options/dramas', { params })
  },

  /** 制作团队枚举（与后端 ProductionTeam 常量一致） */
  getProductionTeams() {
    return api.get<{ list: Array<{ id: number; value: number; label: string; name: string }> }>(
      '/filter-options/production-teams',
    )
  },

  getProjectTypes() {
    return api.get<{ list: Array<{ id: number; label: string }> }>(
      '/filter-options/project-types',
    )
  },

  getSiteIds() {
    return api.get<{ list: Array<{ id: number; label: string }> }>(
      '/filter-options/site-ids',
    )
  },
}

// ============ 社媒推广账号 APIs ============
export const socialPromotionAccountApi = {
  getList(params?: {
    page?: number
    page_size?: number
    account_name?: string
    channel?: string[]
    status?: string
  }) {
    return api.get<{ list: any[]; total: number; page: number; page_size: number }>(
      '/social-promotion-accounts',
      { params },
    )
  },
  options(channel: string) {
    return api.get<{ list: { id: number; channel: string; account_name: string }[] }>(
      '/social-promotion-accounts/options',
      { params: { channel } },
    )
  },
  create(data: {
    channel: string
    account_name: string
    status?: string
    remark?: string
  }) {
    return api.post('/social-promotion-accounts', data)
  },
  update(
    id: number,
    data: { channel?: string; account_name?: string; status?: string; remark?: string },
  ) {
    return api.put(`/social-promotion-accounts/${id}`, data)
  },
  updateStatus(id: number, status: 'enabled' | 'disabled') {
    return api.put(`/social-promotion-accounts/${id}/status`, { status })
  },
}

// ============ Site Config APIs (H5) ============
export const siteConfigApi = {
  list() {
    return api.get<{ list: Array<{ id: number; domain: string; domain_name: string }> }>('/site-configs')
  },
}

// ============ Promotion Link APIs ============
export const promotionLinkApi = {
  /**
   * 获取推广链接列表
   */
  getList(params: {
    page?: number
    page_size?: number
    id?: string
    name?: string
    work_id?: string | number
    platform?: string
    os?: string
    promoter_id?: string
    /** 项目类型：1=APP 2=H5 */
    project_type?: string
  }) {
    return api.get<{ list: any[]; total: number; page: number; page_size: number }>('/promotion-links', { params })
  },

  /**
   * 创建推广链接
   * @param data 推广链接数据（后端会计算 name、h5_link、app_deep_link）
   */
  create(data: {
    landing_page_type?: string
    work_id?: number
    platform: string
    promotion_account?: string
    link_type?: string
    generate_short_link?: boolean
    generate_app_deep_link?: boolean
    language?: string
    os?: string
    objective?: string
    promotion_type?: string
    domain?: string
    pixel_id?: string
    promoter_id: number
    promotion_account_id?: number
  }) {
    return api.post('/promotion-links', data)
  },

  /**
   * 获取推广链接详情
   * @param id 推广链接ID
   */
  getById(id: number) {
    return api.get(`/promotion-links/${id}`)
  },

  regenerateSocialCopy(id: number) {
    return api.post(`/promotion-links/${id}/regenerate-social-copy`)
  },
}

// ============ Audience Package APIs (受众包) ============
export interface AudiencePackageTargetingItem {
  id: string
  name: string
  type: string
}

/** TikTok targeting 子对象（audienceType = original 时生效），见 .superpowers/API.md */
export interface TikTokAudiencePackageTargeting {
  interest_category_ids?: string[]
  spending_power?: "ALL" | "HIGH"
  min_android_version?: string
  min_ios_version?: string
  device_model_ids?: string[]
  network_types?: string[]
  isp_ids?: string[]
  device_price_ranges?: number[]
}

export type AudiencePackageTargeting =
  | AudiencePackageTargetingItem[]
  | TikTokAudiencePackageTargeting

export interface AudiencePackage {
  id: number
  name: string
  platform: string
  audienceType: string
  locationTargeting: string[]
  locationExclusion: string[]
  ageMin: number
  ageMax: number
  gender: string
  languages: string[]
  targeting: AudiencePackageTargeting
  targetingExclusion: AudiencePackageTargetingItem[]
  created_at: string
  updated_at: string
}

export const audiencePackageApi = {
  /**
   * 获取当前用户的受众包列表
   */
  getList(params?: {
    page?: number
    page_size?: number
    platform?: string
    name?: string
  }) {
    return api.get<{ items: AudiencePackage[], total: number, page: number, page_size: number }>('/audience-packages', { params })
  },

  /**
   * 获取单个受众包详情
   */
  getById(id: number) {
    return api.get<AudiencePackage>(`/audience-packages/${id}`)
  },

  /**
   * 创建受众包
   */
  create(data: {
    name: string
    platform: string
    audienceType?: string
    locationTargeting?: string[]
    locationExclusion?: string[]
    ageMin?: number
    ageMax?: number
    age_groups?: string[]
    gender?: string
    languages?: string[]
    targeting?: AudiencePackageTargeting
    targetingExclusion?: AudiencePackageTargetingItem[]
  }) {
    return api.post<AudiencePackage>('/audience-packages', data)
  },

  /**
   * 更新受众包
   */
  update(id: number, data: {
    name?: string
    platform?: string
    audienceType?: string
    locationTargeting?: string[]
    locationExclusion?: string[]
    ageMin?: number
    ageMax?: number
    age_groups?: string[]
    gender?: string
    languages?: string[]
    targeting?: AudiencePackageTargeting
    targetingExclusion?: AudiencePackageTargetingItem[]
  }) {
    return api.put<AudiencePackage>(`/audience-packages/${id}`, data)
  },

  /**
   * 删除受众包
   */
  delete(id: number) {
    return api.delete(`/audience-packages/${id}`)
  }
}


// ============ Monitor Rule APIs (监控规则) ============
export const monitorRuleApi = {
  /**
   * 获取监控规则列表
   */
  getList(params: {
    page?: number
    page_size?: number
    keyword?: string
    status?: string
    platform?: string
    project_type?: string
    action_type?: string
    creator_id?: number
  }) {
    return api.get<{ list: any[]; total: number; page: number }>('/monitor-rules', { params })
  },

  /**
   * 获取监控规则详情
   */
  getById(id: number) {
    return api.get(`/monitor-rules/${id}`)
  },

  /**
   * 创建监控规则
   */
  create(data: {
    name: string
    platform: string
    project_type: string
    level: string
    action_type: string
    trigger_cycles: number
    monitor_time_start?: string
    monitor_time_end?: string
    apply_scope?: string
    apply_target_ids?: string[]
    condition_groups: Array<{
      conditions: Array<{
        metric: string
        operator: string
        value: number | undefined
        time_range?: string
      }>
    }>
  }) {
    return api.post('/monitor-rules', data)
  },

  /**
   * 更新监控规则
   */
  update(id: number, data: {
    name?: string
    platform?: string
    project_type?: string
    level?: string
    action_type?: string
    trigger_cycles?: number
    monitor_time_start?: string
    monitor_time_end?: string
    apply_scope?: string
    apply_target_ids?: string[]
    condition_groups?: Array<{
      conditions: Array<{
        metric: string
        operator: string
        value: number | undefined
        time_range?: string
      }>
    }>
  }) {
    return api.put(`/monitor-rules/${id}`, data)
  },

  /**
   * 删除监控规则
   */
  delete(id: number) {
    return api.delete(`/monitor-rules/${id}`)
  },

  /**
   * 启用/暂停监控规则
   */
  updateStatus(id: number, status: string) {
    return api.put(`/monitor-rules/${id}/status`, { status })
  },

  /**
   * 复制监控规则（支持复制他人规则为自己的）
   */
  copy(id: number) {
    return api.post(`/monitor-rules/${id}/copy`)
  },

  /**
   * 获取规则执行日志
   */
  getLogs(params: {
    rule_id?: number
    target_id?: string
    action?: string
    status?: string
    page?: number
    page_size?: number
    start_date?: string
    end_date?: string
  }) {
    return api.get('/monitor-execution-logs', { params })
  },

  /**
   * 获取监控仪表盘数据
   */
  getDashboard() {
    return api.get('/monitor-dashboard')
  },

  /**
   * 批量获取广告的监控状态
   */
  getMonitorStatus(params: { platform: string; level: string; ids: string }) {
    return api.get('/monitor-status', { params })
  }
}

// ============ Postback Rules APIs ============
export const postbackRuleApi = {
  get(platform: string) {
    return api.get(`/postback-rules/${platform}`)
  },
  getProducts(platform: string) {
    return api.get(`/postback-rules/${platform}/products`)
  },
  refreshProducts(platform: string) {
    return api.post(`/postback-rules/${platform}/products/refresh`)
  },
  save(platform: string, data: {
    enabled?: boolean
    deduction_products: string[]
    deduction_params: Record<string, any>
    downgrade_settings: Record<string, any[]>
  }) {
    return api.post(`/postback-rules/${platform}`, data)
  }
}

// ============ Postback View APIs（广告系列/广告组 抽屉） ============
export interface PostbackViewQuery {
  start?: string
  end?: string
  page?: number
  page_size?: number
}

export const postbackViewApi = {
  /**
   * 激活用户列表（分页）
   */
  getActivationUsers(
    platform: string,
    level: 'campaign' | 'adset',
    id: string | number,
    params: PostbackViewQuery = {},
  ) {
    return api.get(
      `/postback-view/${platform}/${level}/${id}/activation-users`,
      { params },
    )
  },
  /**
   * D0 订单列表（分页）
   */
  getD0Orders(
    platform: string,
    level: 'campaign' | 'adset',
    id: string | number,
    params: PostbackViewQuery = {},
  ) {
    return api.get(
      `/postback-view/${platform}/${level}/${id}/d0-orders`,
      { params },
    )
  },
}

// ============ 手动回传（媒体 S2S 直传） ============
export const manualPostbackApi = {
  retry(data: { platform: string; order_id: string | number }) {
    return api.post('/postback/manual-retry', data)
  },
  mediaDirect(data: { platform: string; order_id: string | number }) {
    return api.post('/postback/media-direct', data)
  }
}

// ============ 分销管理 APIs ============
export const distributionAccountApi = {
  getList(params?: {
    page?: number
    page_size?: number
    account?: string
    channel?: string
    status?: string
  }) {
    return api.get<{ list: any[]; total: number; page: number; page_size: number }>(
      '/distribution-accounts',
      { params },
    )
  },
  create(data: { channel: string; account: string; password: string }) {
    return api.post('/distribution-accounts', data)
  },
  update(id: number, data: { account: string }) {
    return api.put(`/distribution-accounts/${id}`, data)
  },
  updateStatus(id: number, status: 'enabled' | 'disabled') {
    return api.put(`/distribution-accounts/${id}/status`, { status })
  },
  resetPassword(id: number, password: string) {
    return api.put(`/distribution-accounts/${id}/password`, { password })
  },
}

export const distributionWorkApi = {
  getList(params?: {
    page?: number
    page_size?: number
    work_id?: string | number
    keyword?: string
  }) {
    return api.get<{ list: any[]; total: number; page: number; page_size: number }>(
      '/distribution-works',
      { params },
    )
  },
  add(workIds: Array<string | number>) {
    return api.post('/distribution-works', { work_ids: workIds })
  },
  remove(id: number) {
    return api.delete(`/distribution-works/${id}`)
  },
}

// ============ 异步数据导出 APIs ============
export type ExportTaskType =
  | 'overview'
  | 'date'
  | 'campaign'
  | 'optimizer'
  | 'designer'
  | 'drama'
  | 'attribution'
  | 'creative'
  | 'utc8_campaign'
  | 'utc7_campaign'
  | 'daily_trend'
  | 'facebook_ad_account'
  | 'tiktok_ad_account'
  | 'works_info'

export interface ExportTaskItem {
  id: number
  user_id: number
  export_type: ExportTaskType | string
  file_name: string
  file_path?: string
  status: number
  status_text?: string
  operator_name?: string
  error_message?: string
  started_at?: string | null
  finished_at?: string | null
  created_at: string
  updated_at?: string
}

export const exportTaskApi = {
  create(data: { export_type: ExportTaskType | string; params?: Record<string, any> }) {
    return api.post<any, ExportTaskItem>('/export-tasks', data)
  },

  getList(params?: {
    page?: number
    page_size?: number
    status?: string | number
    start_date?: string
    end_date?: string
  }) {
    return api.get<any, { list: ExportTaskItem[]; total: number; page: number; page_size: number }>(
      '/export-tasks',
      { params },
    )
  },

  download(id: number) {
    return api.get(`/export-tasks/${id}/download`, { responseType: 'blob' as const })
  },

  retry(id: number) {
    return api.post<any, ExportTaskItem>(`/export-tasks/${id}/retry`)
  },
}

// ============ Facebook Value Rules APIs ============
export interface ValueRuleCriteria {
  id?: string
  criteria_type: string
  operator: string
  criteria_values: string[]
  criteria_value_types: string[]
}

export interface ValueRuleItem {
  id?: string
  name?: string
  adjust_sign: 'INCREASE' | 'DECREASE' | string
  adjust_value: number
  status?: string
  criterias: ValueRuleCriteria[]
}

export interface ValueRuleSetItem {
  id: number
  account_id: string
  fb_rule_set_id: string
  name: string
  rules: ValueRuleItem[]
  synced_at?: string | null
  created_by?: number
  created_at?: string
  updated_at?: string
}

export interface ValueRuleCriteriaOption {
  value: string
  label: string
  enabled?: boolean
  disabled_reason?: string
  has_children?: boolean
  value_type?: string
  children?: ValueRuleCriteriaOption[]
}

export interface ValueRuleCriteriaOptions {
  criteria_types: Array<{ value: string; label: string }>
  options: Record<string, ValueRuleCriteriaOption[]>
  operators: Array<{ value: string; label: string }>
  adjust_signs: Array<{ value: string; label: string }>
  account_id?: string
}

export const valueRuleApi = {
  getList(params?: {
    page?: number
    page_size?: number
    account_id?: string
    name?: string
    keyword?: string
  }) {
    return api.get<any, { list: ValueRuleSetItem[]; total: number; page: number; page_size: number }>(
      '/value-rules',
      { params },
    )
  },

  getCriteriaOptions(params?: { account_id?: string }) {
    return api.get<any, ValueRuleCriteriaOptions>('/value-rules/criteria-options', {
      params,
    })
  },

  sync(data?: { account_ids?: string[] }) {
    return api.post<any, { synced: number; accounts: number; errors: string[] }>(
      '/value-rules/sync',
      data || {},
    )
  },

  create(data: { account_id: string; name: string; rules: ValueRuleItem[] }) {
    return api.post<any, ValueRuleSetItem>('/value-rules', data)
  },

  update(id: number, data: { name: string; rules: ValueRuleItem[] }) {
    return api.put<any, ValueRuleSetItem>(`/value-rules/${id}`, data)
  },

  delete(id: number) {
    return api.delete(`/value-rules/${id}`)
  },
}
